import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import { View, ScrollView, StyleSheet, Text, Linking } from "react-native"
import * as amplitude from "@amplitude/analytics-react-native"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import BrevetFunnelHeader from "./components/BrevetFunnelHeader"
import FunnelEmbeddedCheckout from "./components/FunnelEmbeddedCheckout"
import BrevetFormulaCard from "./components/BrevetFormulaCard"
import BrevetChip from "./components/BrevetChip"
import TimeUntilMidnightCountdown from "./components/TimeUntilMidnightCountdown"
import { useBrevetFunnel } from "./context/BrevetFunnelContext"
import {
    BREVET_FORMULAS_LIST,
    resolveFormula,
} from "./constants/brevetFormulas"
import {
    getBookableSlots,
    getNextBookableSlot,
    formatFirstSessionLabel,
    getSlotById,
    getAllowedFormulas,
    getFormulaEconomics,
    isFunnelClosed,
} from "./constants/brevetSlots"
import { recommendFormula } from "./utils/recommendFormula"
import { formatEuros } from "./utils/formatEuros"
import { useMidnightOfferActive } from "./utils/midnightOffer"
import { brevetFunnelService } from "./services/brevetFunnelService"

const BrevetPricingCheckoutScreen = ({ navigation }) => {
    const dimensions = useContext(DimensionsContext)
    const { state, update } = useBrevetFunnel()
    const scrollRef = useRef(null)

    // "Now" stable pour toute la durée du checkout (≤ 5 min). Évite qu'une phase
    // bascule en plein milieu du flow utilisateur. On ne ré-évalue pas en live.
    const now = useMemo(() => new Date().toISOString(), [])

    const funnelClosed = useMemo(() => isFunnelClosed(now), [now])
    const allowedFormulas = useMemo(() => getAllowedFormulas(now), [now])
    const bookableSlots = useMemo(() => getBookableSlots(now), [now])

    const reco = useMemo(
        () =>
            recommendFormula({
                subjects: state.subjects,
                objective: state.objective,
                brevetBlanc: state.brevetBlanc,
                niveauActuel: state.niveauActuel,
                autonomie: state.autonomie,
                timePerWeekend: state.timePerWeekend,
            }),
        [
            state.subjects,
            state.objective,
            state.brevetBlanc,
            state.niveauActuel,
            state.autonomie,
            state.timePerWeekend,
        ]
    )

    // Initial selected formula: prefer prior state, else reco, else fallback to
    // first allowed. If none allowed (closed funnel), null is OK — we won't render
    // the pricing block in that case.
    const initialFormulaId = useMemo(() => {
        const candidates = [state.selectedFormulaId, reco.formula.id]
        for (const candidate of candidates) {
            if (candidate && allowedFormulas.includes(candidate)) return candidate
        }
        return (
            BREVET_FORMULAS_LIST.find((f) => allowedFormulas.includes(f.id))?.id ||
            null
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const [selectedFormulaId, setSelectedFormulaId] = useState(initialFormulaId)

    // Si la phase active change la liste des formules autorisées (ex: bascule
    // phase 1 → phase 2 entre 2 mounts), s'assurer que le selected reste valide.
    useEffect(() => {
        if (!selectedFormulaId) return
        if (allowedFormulas.includes(selectedFormulaId)) return
        const fallback =
            BREVET_FORMULAS_LIST.find((f) => allowedFormulas.includes(f.id))?.id ||
            null
        setSelectedFormulaId(fallback)
    }, [allowedFormulas, selectedFormulaId])

    // Slot chips visible to the user depend on the selected formula.
    // Découverte / Sprint show mono-slots ; Intensif shows the 3 composite slots.
    const visibleSlots = useMemo(
        () =>
            bookableSlots.filter((s) =>
                (s.applicable_formulas || []).includes(selectedFormulaId)
            ),
        [bookableSlots, selectedFormulaId]
    )

    const [selectedSlotId, setSelectedSlotId] = useState(() => {
        const stateSlot = visibleSlots.find(
            (s) => s.id === state.selectedSlotId && s.available
        )
        return (
            stateSlot?.id ||
            visibleSlots.find((s) => s.available)?.id ||
            null
        )
    })

    const [clientSecret, setClientSecret] = useState(null)
    const [creatingSession, setCreatingSession] = useState(false)
    const [sessionError, setSessionError] = useState(null)

    // Formules résolues = base (label/pitch/id) overlay des économies de la
    // phase active (prix / heures / nb WE cohorte-aware). Source de vérité du
    // pricing affiché. Recalculé seulement si `now` change (stable sur le flow).
    const resolvedFormulas = useMemo(
        () =>
            BREVET_FORMULAS_LIST.map((f) =>
                resolveFormula(f.id, getFormulaEconomics(f.id, now))
            ).filter(Boolean),
        [now]
    )
    const formula = useMemo(
        () =>
            selectedFormulaId
                ? resolvedFormulas.find((f) => f.id === selectedFormulaId) || null
                : null,
        [resolvedFormulas, selectedFormulaId]
    )
    const slot = useMemo(
        () => (selectedSlotId ? getSlotById(selectedSlotId, now) : null),
        [selectedSlotId, now]
    )

    const referralActive = !!state.referralCode
    const offerActive = useMidnightOfferActive()
    // Pricing modes: referral (-50% on top), midnight-offer (50% off vs strike),
    // or plain (no strike). referral wins over offer when both could apply.
    const pricingMode = referralActive ? "referral" : offerActive ? "offer" : "plain"
    const displayedPrice = formula
        ? pricingMode === "referral"
            ? formula.price / 2
            : formula.price
        : 0
    const originalPrice = formula
        ? {
              referral: formula.price,
              offer: formula.price * 2,
              plain: null,
          }[pricingMode]
        : null
    const displayedDeposit = formula?.deposit ?? 0
    const displayedRemainder = formula
        ? pricingMode === "referral"
            ? Math.max(0, displayedPrice - displayedDeposit)
            : formula.remainder
        : 0
    const originalRemainder = formula
        ? {
              referral: formula.remainder,
              offer: formula.remainder * 2,
              plain: null,
          }[pricingMode]
        : null

    // Amplitude — pricing screen view (1x au mount, prop sources stables).
    useEffect(() => {
        amplitude.track("brevet_pricing_viewed", {
            funnel_state_id: state.funnelStateId,
            formula_id: selectedFormulaId,
            slot_id: selectedSlotId,
            objective: state.objective,
            subjects: state.subjects,
            time_per_weekend: state.timePerWeekend,
            referral_code: state.referralCode || null,
            funnel_closed: funnelClosed,
            allowed_formulas: allowedFormulas,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Création (ou recréation) de la checkout session quand la formule / slot changent.
    useEffect(() => {
        // Drop any previous Stripe session BEFORE the early returns. Otherwise a
        // user switching formula → no compatible slot keeps a stale clientSecret
        // mounted, and the next "Réserver" click pays for the previous formula.
        setClientSecret(null)
        if (funnelClosed) return
        if (!state.funnelStateId) return
        if (!selectedFormulaId || !selectedSlotId) return
        // Guard: slot may be stale for 1 render after formula switch.
        if (!visibleSlots.some((s) => s.id === selectedSlotId)) return

        let cancelled = false
        setCreatingSession(true)
        setSessionError(null)
        brevetFunnelService
            .createCheckoutSession(
                { stateId: state.funnelStateId },
                {
                    formula_id: selectedFormulaId,
                    slot_id: selectedSlotId,
                }
            )
            .then((res) => {
                if (cancelled) return
                // Laravel Resources wrap payload under `data`. Axios's res.data is the envelope.
                const secret =
                    res?.data?.data?.client_secret ?? res?.data?.client_secret
                if (secret) {
                    setClientSecret(secret)
                } else {
                    setSessionError(
                        "Impossible d'initialiser le paiement. Réessayez."
                    )
                }
            })
            .catch(() => {
                if (cancelled) return
                setSessionError(
                    "Impossible d'initialiser le paiement. Réessayez."
                )
            })
            .finally(() => {
                if (!cancelled) setCreatingSession(false)
            })

        return () => {
            cancelled = true
        }
    }, [funnelClosed, state.funnelStateId, selectedFormulaId, selectedSlotId, visibleSlots])

    // Si le slot sélectionné n'est plus bookable, propose le suivant.
    useEffect(() => {
        if (!slot || slot.available) return
        const next = getNextBookableSlot(visibleSlots, selectedSlotId, now)
        if (next) setSelectedSlotId(next.id)
    }, [slot, selectedSlotId, visibleSlots, now])

    // Auto-bascule when the user switches formula: if the previously-selected
    // slot is no longer visible (e.g. switching from Intensif → Sprint), pick
    // the first available visible slot.
    useEffect(() => {
        const stillVisible = visibleSlots.some(
            (s) => s.id === selectedSlotId && s.available
        )
        if (stillVisible) return
        const fallback = visibleSlots.find((s) => s.available)
        setSelectedSlotId(fallback?.id || null)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFormulaId, visibleSlots])

    const containerWidth = Math.min(dimensions?.width || 400, 460)
    const firstSessionLabel = slot ? formatFirstSessionLabel(slot) : null

    const onPaymentSuccess = (paymentIntentId) => {
        amplitude.track("brevet_payment_succeeded", {
            funnel_state_id: state.funnelStateId,
            formula_id: selectedFormulaId,
            slot_id: selectedSlotId,
            objective: state.objective,
            subjects: state.subjects,
            referral_code: state.referralCode || null,
        })
        update({
            selectedFormulaId,
            selectedSlotId,
            paid: true,
            paymentIntentId,
        })
        navigation.navigate("brevet/confirmation")
    }

    const onPaymentError = (errorCode) => {
        amplitude.track("brevet_payment_failed", {
            funnel_state_id: state.funnelStateId,
            formula_id: selectedFormulaId,
            slot_id: selectedSlotId,
            error_code: errorCode || "unknown",
            referral_code: state.referralCode || null,
        })
    }

    // Funnel fermé (aucune phase active) — affiche un état "Sessions complètes"
    // plutôt que la pricing card cassée. Le lead garde une issue : landing site.
    if (funnelClosed) {
        return (
            <View style={styles.root}>
                <View style={[styles.frame, { width: containerWidth }]}>
                    <BrevetFunnelHeader
                        step={4}
                        total={4}
                        onBack={() => navigation.goBack()}
                    />
                    <ScrollView
                        ref={scrollRef}
                        contentContainerStyle={styles.content}
                        showsVerticalScrollIndicator={false}
                    >
                        <View style={styles.closedNotice}>
                            <FontAwesome5
                                name="info-circle"
                                size={22}
                                color={theme.colors.primary}
                                style={{ marginBottom: 10 }}
                            />
                            <Text style={styles.closedTitle}>
                                Sessions complètes
                            </Text>
                            <Text style={styles.closedText}>
                                Les inscriptions au stage de révision Brevet
                                sont closes pour cette session. Pour toute
                                demande, contactez-nous via{" "}
                                <Text
                                    style={styles.closedLink}
                                    onPress={() =>
                                        Linking.openURL(
                                            "https://fr.masteur.com/brevet"
                                        )
                                    }
                                >
                                    fr.masteur.com/brevet
                                </Text>
                                .
                            </Text>
                        </View>
                    </ScrollView>
                </View>
            </View>
        )
    }

    const sharedSummary = (
        <>
            {offerActive && !referralActive ? (
                <View style={styles.heroUrgency}>
                    <View style={styles.heroBadge}>
                        <Text style={styles.heroBadgeText}>−50 %</Text>
                    </View>
                    <View style={styles.heroTextWrap}>
                        <Text style={styles.heroTitle}>
                            Offre flash, expire à minuit
                        </Text>
                        <TimeUntilMidnightCountdown message="" inline />
                    </View>
                </View>
            ) : null}

            {referralActive ? (
                <View style={styles.referralBanner}>
                    <Text style={styles.referralBannerText}>
                        🎁{" "}
                        <Text style={styles.referralBannerBold}>
                            -50 € appliqués
                        </Text>
                        {state.referrerFirstname
                            ? ` grâce à ${state.referrerFirstname}`
                            : ""}
                        .
                    </Text>
                </View>
            ) : null}

            <Text style={styles.sectionTitle}>Formule</Text>
            {resolvedFormulas.map((f) => {
                const isAllowed = allowedFormulas.includes(f.id)
                return (
                    <BrevetFormulaCard
                        key={f.id}
                        formula={f}
                        selected={selectedFormulaId === f.id}
                        recommended={reco.formula.id === f.id && isAllowed}
                        disabled={!isAllowed}
                        onPress={() => setSelectedFormulaId(f.id)}
                        discount={offerActive && !referralActive}
                        referralActive={referralActive}
                    />
                )
            })}

            <Text style={[styles.sectionTitle, { marginTop: 18 }]}>
                Créneau
            </Text>
            <View style={styles.chipsRow}>
                {visibleSlots.map((s) => (
                    <BrevetChip
                        key={s.id}
                        label={s.label}
                        selected={selectedSlotId === s.id}
                        disabled={!s.available}
                        onPress={() => setSelectedSlotId(s.id)}
                    />
                ))}
            </View>

            {slot && firstSessionLabel ? (
                <View style={styles.firstSessionBox}>
                    <Text style={styles.firstSessionTitle}>
                        Premier cours : {firstSessionLabel}
                    </Text>
                    {slot.is_composite ? (
                        <Text style={styles.firstSessionScheduleSub}>
                            Créneau : {slot.label}
                        </Text>
                    ) : null}
                    <View style={styles.firstSessionRow}>
                        <FontAwesome5
                            name="video"
                            size={11}
                            color={theme.colors.primary}
                        />
                        <Text style={styles.firstSessionSub}>
                            Cours 100 % en ligne en visio sur l'app Masteur.
                        </Text>
                    </View>
                    <View style={styles.firstSessionRow}>
                        <FontAwesome5
                            name="mobile-alt"
                            size={11}
                            color={theme.colors.primary}
                        />
                        <Text style={styles.firstSessionSub}>
                            Lien d'accès envoyé par email la veille du cours.
                        </Text>
                    </View>
                </View>
            ) : null}

            {formula ? (
                <View style={styles.summary}>
                    <View style={styles.summaryRow}>
                        <Text style={[styles.summaryLabel, { flex: 1 }]}>
                            À régler aujourd'hui
                        </Text>
                        <View style={styles.summaryValueGroup}>
                            <Text style={styles.summaryStrike}>
                                {formatEuros(displayedPrice)}
                            </Text>
                            <Text style={styles.summaryDeposit}>
                                {formatEuros(displayedDeposit)}
                            </Text>
                        </View>
                    </View>
                    <View
                        style={[
                            styles.summaryRow,
                            { borderBottomWidth: 0, paddingBottom: 0 },
                        ]}
                    >
                        <Text style={[styles.summaryLabel, { flex: 1 }]}>
                            Prélèvement automatique{"\n"}(après le 1er cours)
                        </Text>
                        <View style={styles.summaryValueGroup}>
                            {originalRemainder !== null ? (
                                <Text style={styles.summaryStrike}>
                                    {formatEuros(originalRemainder)}
                                </Text>
                            ) : null}
                            <Text style={styles.summaryValue}>
                                {formatEuros(displayedRemainder)}
                            </Text>
                        </View>
                    </View>
                </View>
            ) : null}
        </>
    )

    return (
        <View style={styles.root}>
            <View style={[styles.frame, { width: containerWidth }]}>
                <BrevetFunnelHeader
                    step={4}
                    total={4}
                    onBack={() => navigation.goBack()}
                />
                <FunnelEmbeddedCheckout
                    clientSecret={clientSecret}
                    summary={sharedSummary}
                    buttonLabel={`Réserver pour ${formatEuros(
                        displayedDeposit
                    )}`}
                    guarantees={[
                        "Annulable en 1 clic sans condition jusqu'à J-7",
                        "Prélèvement automatique après le 1er cours",
                        "Tuteurs notés 4,9/5 (25 000+ h de cours)",
                    ]}
                    onSuccess={onPaymentSuccess}
                    onError={onPaymentError}
                    creatingSession={creatingSession}
                    sessionError={sessionError}
                    containerWidth={containerWidth}
                    nativeNotice="Le paiement se finalise depuis un navigateur. Ouvrez fr.masteur.com/brevet pour terminer votre réservation."
                    scrollRef={scrollRef}
                />
            </View>
        </View>
    )
}

const styles = StyleSheet.create({
    root: {
        flex: 1,
        alignItems: "center",
        backgroundColor: theme.colors.whiteAlmost,
    },
    frame: {
        flex: 1,
        backgroundColor: theme.colors.white,
        paddingHorizontal: 20,
    },
    content: {
        paddingTop: 14,
        paddingBottom: 24,
    },
    heroUrgency: {
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        backgroundColor: theme.colors.error,
        paddingHorizontal: 14,
        paddingVertical: 12,
        borderRadius: 12,
        marginBottom: 14,
    },
    heroBadge: {
        backgroundColor: theme.colors.white,
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 8,
    },
    heroBadgeText: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 17,
        color: theme.colors.error,
        letterSpacing: 0.5,
    },
    heroTextWrap: {
        flex: 1,
    },
    heroTitle: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 13,
        color: theme.colors.white,
        marginBottom: 2,
    },
    referralBanner: {
        backgroundColor: theme.colors.successLight,
        borderRadius: 10,
        paddingHorizontal: 12,
        paddingVertical: 8,
        marginBottom: 14,
    },
    referralBannerText: {
        fontFamily: "Montserrat-Regular",
        fontSize: 12,
        color: theme.colors.successDark,
    },
    referralBannerBold: {
        fontFamily: "Montserrat-Bold",
    },
    sectionTitle: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 18,
        color: theme.colors.primary,
        marginBottom: 10,
    },
    chipsRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 8,
    },
    firstSessionBox: {
        marginTop: 10,
        padding: 12,
        backgroundColor: theme.colors.primaryLighter,
        borderRadius: 10,
    },
    firstSessionTitle: {
        fontFamily: "Montserrat-Bold",
        fontSize: 13,
        color: theme.colors.primary,
        marginBottom: 8,
    },
    firstSessionScheduleSub: {
        fontFamily: "Montserrat-Medium",
        fontSize: 12,
        color: theme.colors.primary,
        marginBottom: 8,
    },
    firstSessionRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        marginTop: 4,
    },
    firstSessionSub: {
        flex: 1,
        fontFamily: "Montserrat-Regular",
        fontSize: 12,
        color: theme.colors.grey,
    },
    summary: {
        marginTop: 14,
    },
    summaryRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        paddingVertical: 10,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.lightMore,
    },
    summaryLabel: {
        fontFamily: "Montserrat-Regular",
        fontSize: 13,
        color: theme.colors.grey,
    },
    summaryValue: {
        fontFamily: "Montserrat-Regular",
        fontSize: 13,
        color: theme.colors.grey,
    },
    summaryValueGroup: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
    },
    summaryStrike: {
        fontFamily: "Montserrat-Regular",
        fontSize: 13,
        color: theme.colors.light,
        textDecorationLine: "line-through",
    },
    summaryDeposit: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 15,
        color: theme.colors.success,
    },
    closedNotice: {
        marginTop: 40,
        padding: 24,
        backgroundColor: theme.colors.primaryLighter,
        borderRadius: 12,
        alignItems: "center",
    },
    closedTitle: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 18,
        color: theme.colors.primary,
        marginBottom: 10,
        textAlign: "center",
    },
    closedText: {
        fontFamily: "Montserrat-Medium",
        fontSize: 13,
        color: theme.colors.grey,
        lineHeight: 19,
        textAlign: "center",
    },
    closedLink: {
        fontFamily: "Montserrat-Bold",
        color: theme.colors.primary,
        textDecorationLine: "underline",
    },
})

export default BrevetPricingCheckoutScreen
